import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, RefreshCw, Search } from 'lucide-react';
import { apiService } from '../../../services/api';
import { parseProjectJournalLogMessage } from '../../../utils/stockLogLinks';
import { Card, Empty, GhostButton, Label } from '../ui';
import { ErrorText, errorText, useNexusProjectRefresh, useTabError } from './shared';

// Occurrences d'inventaire rattachées au projet : liste filtrable à gauche, détail de l'occurrence choisie à droite
// (champs modifiables sur place, sous-items dépliables, journal de chaque occurrence).

const STATUS_OPTIONS = [
  { value: 'ordered', label: 'Commandé' },
  { value: 'in_stock', label: 'En stock' },
  { value: 'at_client', label: 'Chez le propriétaire' },
  { value: 'removed', label: 'Supprimé' },
];

const STATUS_TONES = {
  ordered: 'border-amber-200 bg-amber-50 text-amber-700',
  in_stock: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  at_client: 'border-sky-200 bg-sky-50 text-sky-700',
  removed: 'border-rose-200 bg-rose-50 text-rose-700',
};

const SEARCH_FIELDS = ['serial_number', 'item_name', 'occurrence_name', 'location', 'notes', 'tracking_number', 'client_name', 'project_name'];
const EDITABLE_FIELDS = ['occurrence_name', 'status', 'location', 'notes'];
const STOCK_ENTITIES = ['inventory', 'stock', 'inventory_stock'];

const inputClass = 'w-full min-w-0 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100';

const statusLabel = (status) => STATUS_OPTIONS.find((option) => option.value === status)?.label || status;
const itemLabel = (item) => (item.occurrence_name ? `${item.item_name || ''} - ${item.occurrence_name}` : item.item_name || '');
const itemTitle = (item) => `${item.serial_number || `#${item.id}`} - ${itemLabel(item)}`;
const dateTime = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '');

const Field = ({ label, className = '', children }) => (
  <label className={`min-w-0 space-y-1 ${className}`}>
    <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
    {children}
  </label>
);

const StatusOptions = () => STATUS_OPTIONS.map((option) => (
  <option key={option.value} value={option.value}>{option.label}</option>
));

// Une note du journal d'occurrence peut citer une entrée du journal projet : elle devient un lien.
const LogMessage = ({ message }) => {
  const { text, link } = parseProjectJournalLogMessage(message || '');
  if (!link) return <p className="break-words text-slate-700">{text}</p>;
  return (
    <Link to={`/project/${link.projectId}?panel=journal&entryId=${link.entryId}`}
      className="break-words text-slate-700 underline decoration-dotted hover:text-indigo-600">
      {text || 'Voir le journal'}
    </Link>
  );
};

const ItemsTab = ({ projectId }) => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [children, setChildren] = useState({});
  const [loadingChildren, setLoadingChildren] = useState({});
  const [expanded, setExpanded] = useState({});
  const [logs, setLogs] = useState({});
  const [logDrafts, setLogDrafts] = useState({});
  const [edits, setEdits] = useState({});
  const pendingLogs = useRef(new Set());
  const { error, fail } = useTabError();

  const loadStock = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const data = await apiService.get(`/inventory/stock?project_id=${projectId}`);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setItems([]);
      fail(`Chargement des items : ${errorText(err)}`);
    } finally {
      setLoading(false);
    }
  }, [projectId, fail]);

  const loadChildren = useCallback(async (stockId) => {
    if (!stockId) return;
    setLoadingChildren((prev) => ({ ...prev, [stockId]: true }));
    try {
      const data = await apiService.get(`/inventory/stock/${stockId}/components`);
      setChildren((prev) => ({ ...prev, [stockId]: Array.isArray(data) ? data : [] }));
    } catch (err) {
      setChildren((prev) => ({ ...prev, [stockId]: [] }));
      fail(`Sous-items : ${errorText(err)}`);
    } finally {
      setLoadingChildren((prev) => ({ ...prev, [stockId]: false }));
    }
  }, [fail]);

  const loadLogs = useCallback(async (stockId) => {
    if (!stockId) return;
    pendingLogs.current.add(stockId);
    try {
      const data = await apiService.get(`/inventory/stock/${stockId}/logs`);
      setLogs((prev) => ({ ...prev, [stockId]: Array.isArray(data) ? data : [] }));
    } catch (err) {
      setLogs((prev) => ({ ...prev, [stockId]: [] }));
      fail(`Journal de l'item : ${errorText(err)}`);
    } finally {
      pendingLogs.current.delete(stockId);
    }
  }, [fail]);

  useEffect(() => { loadStock(); }, [loadStock]);

  // « Actualiser » et Nexus rechargent la liste et le détail ouvert.
  const refresh = useCallback(() => {
    loadStock();
    if (selectedId) {
      loadChildren(selectedId);
      loadLogs(selectedId);
    }
  }, [loadStock, loadChildren, loadLogs, selectedId]);

  useNexusProjectRefresh(projectId, (entity) => {
    if (STOCK_ENTITIES.includes(entity)) refresh();
  });

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) => SEARCH_FIELDS.map((key) => item[key]).filter(Boolean).join(' ').toLowerCase().includes(needle));
  }, [items, search]);

  const selected = useMemo(() => filtered.find((item) => item.id === selectedId) || null, [filtered, selectedId]);
  const rootId = selected?.id ?? null;

  // L'occurrence choisie disparaît du filtre : on la désélectionne.
  useEffect(() => {
    if (selectedId && !filtered.some((item) => item.id === selectedId)) setSelectedId(null);
  }, [filtered, selectedId]);

  useEffect(() => {
    if (!rootId) return;
    loadChildren(rootId);
    loadLogs(rootId);
  }, [rootId, loadChildren, loadLogs]);

  // Le journal de chaque sous-item visible est chargé une fois.
  const visibleIds = useMemo(() => {
    if (!rootId) return [];
    const ids = new Set();
    const stack = [rootId];
    while (stack.length) {
      const current = stack.pop();
      if (current && !ids.has(current)) {
        ids.add(current);
        if (current === rootId || expanded[current]) {
          (children[current] || []).forEach((child) => {
            if (child?.id) stack.push(child.id);
          });
        }
      }
    }
    return [...ids];
  }, [rootId, children, expanded]);

  useEffect(() => {
    visibleIds.forEach((id) => {
      if (!Object.prototype.hasOwnProperty.call(logs, id) && !pendingLogs.current.has(id)) loadLogs(id);
    });
  }, [visibleIds, logs, loadLogs]);

  const dropEdits = useCallback((stockId) => setEdits((prev) => {
    if (!prev[stockId]) return prev;
    const next = { ...prev };
    delete next[stockId];
    return next;
  }), []);

  // Mise à jour optimiste, dans la liste comme dans les sous-items déjà chargés ; annulée si l'enregistrement échoue.
  const applyUpdate = useCallback((stockId, fields) => {
    const patch = (item) => (item.id === stockId ? { ...item, ...fields } : item);
    setItems((prev) => prev.map(patch));
    setChildren((prev) => Object.fromEntries(
      Object.entries(prev).map(([parentId, list]) => [parentId, Array.isArray(list) ? list.map(patch) : list]),
    ));
  }, []);

  const saveStock = useCallback(async (item, updates) => {
    if (!item?.id || !updates) return;
    const previous = { ...item };
    setSavingId(item.id);
    applyUpdate(item.id, updates);
    try {
      await apiService.patch(`/inventory/stock/${item.id}`, updates);
      dropEdits(item.id);
    } catch (err) {
      applyUpdate(item.id, previous);
      fail(`Mise à jour de l'item : ${errorText(err)}`);
    } finally {
      setSavingId((prev) => (prev === item.id ? null : prev));
    }
  }, [applyUpdate, dropEdits, fail]);

  const valueOf = (item, key) => edits[item.id]?.[key] ?? item[key] ?? '';
  const setEdit = (stockId, key, value) => setEdits((prev) => ({ ...prev, [stockId]: { ...prev[stockId], [key]: value } }));

  // Enregistré en quittant le champ, seulement si quelque chose a changé.
  const persistInline = (item) => {
    if (!edits[item.id]) return;
    const next = Object.fromEntries(EDITABLE_FIELDS.map((key) => [key, valueOf(item, key)]));
    const changed = next.status !== item.status
      || ['occurrence_name', 'location', 'notes'].some((key) => (next[key] || '') !== (item[key] || ''));
    if (!changed) {
      dropEdits(item.id);
      return;
    }
    saveStock(item, {
      occurrence_name: next.occurrence_name || null,
      status: next.status,
      location: next.location || null,
      notes: next.notes || null,
    });
  };

  const toggleNode = (stockId) => {
    const isOpen = Boolean(expanded[stockId]);
    if (!isOpen) loadChildren(stockId);
    loadLogs(stockId);
    setExpanded((prev) => {
      const next = { ...prev };
      if (isOpen) delete next[stockId];
      else next[stockId] = true;
      return next;
    });
  };

  const addLog = async (stockId) => {
    const message = logDrafts[stockId];
    if (!message?.trim()) return;
    try {
      await apiService.post(`/inventory/stock/${stockId}/logs`, { message });
      setLogDrafts((prev) => ({ ...prev, [stockId]: '' }));
      loadLogs(stockId);
    } catch (err) {
      fail(`Ajout de la note : ${errorText(err)}`);
    }
  };

  const inlineProps = (item, key) => ({
    value: valueOf(item, key),
    onChange: (event) => setEdit(item.id, key, event.target.value),
    onBlur: () => persistInline(item),
    className: inputClass,
  });

  const renderFields = (item) => (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
      <Field label="Nom"><input type="text" placeholder="Optionnel" {...inlineProps(item, 'occurrence_name')} /></Field>
      <Field label="Statut"><select {...inlineProps(item, 'status')}><StatusOptions /></select></Field>
      <Field label="Emplacement"><input type="text" {...inlineProps(item, 'location')} /></Field>
      <Field label="Notes" className="sm:col-span-3"><textarea rows={1} {...inlineProps(item, 'notes')} /></Field>
      {savingId === item.id && <p className="text-[11px] text-slate-400 sm:col-span-3">Sauvegarde…</p>}
    </div>
  );

  const renderLogs = (item) => {
    const itemLogs = logs[item.id] || [];
    return (
      <div className="min-w-0 rounded-xl bg-slate-50 p-3 text-[11px] text-slate-500">
        <Label>Journal</Label>
        <div className="mt-2 max-h-48 space-y-1 overflow-auto">
          {itemLogs.map((log) => (
            <div key={log.id} className="rounded-lg border border-slate-200/80 bg-white px-2 py-1">
              <LogMessage message={log.message} />
              <p className="text-[10px] text-slate-400">{log.created_by || '-'} · {dateTime(log.created_at)}</p>
            </div>
          ))}
          {itemLogs.length === 0 && <p className="text-slate-400">Aucune note</p>}
        </div>
        <form className="mt-2 flex gap-1" onSubmit={(event) => { event.preventDefault(); addLog(item.id); }}>
          <input type="text" placeholder="Ajouter une note" value={logDrafts[item.id] || ''}
            onChange={(event) => setLogDrafts((prev) => ({ ...prev, [item.id]: event.target.value }))}
            className={`flex-1 ${inputClass}`} />
          <button type="submit" className="rounded-lg bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-slate-700">
            OK
          </button>
        </form>
      </div>
    );
  };

  // L'occurrence choisie est toujours dépliée ; ses sous-items se déplient un par un.
  const renderNode = (item, depth = 0) => {
    const nodeChildren = children[item.id] || [];
    const isOpen = depth === 0 || Boolean(expanded[item.id]);
    return (
      <div key={item.id} className={`min-w-0 rounded-xl border border-slate-200/80 bg-white p-3 ${depth > 0 ? 'mt-3' : ''}`}>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="break-words text-sm font-semibold text-slate-800">{itemTitle(item)}</p>
            {item.location && <p className="text-[11px] text-slate-500">Emplacement : {item.location}</p>}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${STATUS_TONES[item.status] || 'border-slate-200 bg-slate-100 text-slate-600'}`}>
              {statusLabel(item.status)}
            </span>
            {depth > 0 && item.child_count > 0 && (
              <GhostButton onClick={() => toggleNode(item.id)}>
                {isOpen ? 'Masquer' : `Sous-items (${item.child_count})`}
              </GhostButton>
            )}
          </div>
        </div>
        <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="min-w-0">
            {renderFields(item)}
            {isOpen && (
              <div className="mt-2 space-y-2 border-l border-slate-200 pl-3">
                {loadingChildren[item.id] && <p className="text-[11px] text-slate-400">Chargement…</p>}
                {!loadingChildren[item.id] && nodeChildren.length === 0 && (
                  <p className="text-[11px] text-slate-400">Aucun sous-item.</p>
                )}
                {nodeChildren.map((child) => renderNode(child, depth + 1))}
              </div>
            )}
          </div>
          {renderLogs(item)}
        </div>
      </div>
    );
  };

  return (
    <Card
      title="Items du projet"
      icon={Package}
      action={(
        <GhostButton onClick={refresh} disabled={loading}>
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Actualiser
        </GhostButton>
      )}
    >
      <ErrorText className="mb-3">{error}</ErrorText>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative min-w-0 flex-1 sm:max-w-md">
          <Search size={15} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" value={search} onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher un item (numéro, nom, notes…)"
            className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-8 pr-3 text-sm outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100" />
        </div>
        <span className="text-xs text-slate-400">
          {filtered.length} occurrence{filtered.length > 1 ? 's' : ''}
        </span>
      </div>

      {loading && items.length === 0 ? (
        <p className="mt-3 text-sm text-slate-400">Chargement…</p>
      ) : (
        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)]">
          <div className="min-w-0 space-y-2">
            {filtered.length === 0 && <Empty>Aucun item associé au projet.</Empty>}
            {filtered.map((item) => (
              <div key={item.id} role="button" tabIndex={0}
                onClick={() => setSelectedId(item.id)}
                onKeyDown={(event) => { if (event.key === 'Enter') setSelectedId(item.id); }}
                className={`cursor-pointer rounded-xl border px-3 py-2.5 transition ${item.id === selectedId
                  ? 'border-indigo-300 bg-indigo-50/40 ring-1 ring-indigo-100'
                  : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-800" title={itemTitle(item)}>{itemTitle(item)}</p>
                    <p className="mt-0.5 truncate text-xs text-slate-500">
                      {item.location ? `Emplacement : ${item.location}` : 'Emplacement non défini'}
                    </p>
                  </div>
                  <select value={item.status} disabled={savingId === item.id}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => saveStock(item, { status: event.target.value })}
                    className={`shrink-0 rounded-full border px-2 py-1 text-[11px] font-semibold outline-none ${STATUS_TONES[item.status] || 'border-slate-200 bg-white text-slate-600'}`}>
                    <StatusOptions />
                  </select>
                </div>
                {savingId === item.id && <p className="mt-1 text-[11px] text-slate-400">Sauvegarde en cours…</p>}
              </div>
            ))}
          </div>
          <div className="min-w-0">
            {selected ? renderNode(selected) : <Empty>Sélectionnez une occurrence pour voir son détail.</Empty>}
          </div>
        </div>
      )}
    </Card>
  );
};

export default ItemsTab;
